import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle2, XCircle, AlertCircle, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { rowsToCSV, downloadCSV } from "@/lib/csv";
import { chunkedImport } from "@/lib/chunkedImport";

export default function ImportCSVDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, rows }
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [job, setJob] = useState(null); // async job status
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setCommitted(null); setLoading(false); setCommitting(false);
    setJob(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const downloadTemplate = async (fmt) => {
    try {
      const { data } = await api.get(`/admin/books/import-template?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "csv" ? "import-template.csv" : "import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const close = () => { reset(); onOpenChange(false); };

  const runPreview = async (chosen) => {
    if (!chosen) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", chosen);
      // preview_limit=500 tells the server to parse only the first 500 rows
      // so the preview request always completes well under Cloudflare's 100s edge timeout,
      // even for 200,000-row files. The commit path uses /catalog/import-async which
      // processes the entire file in a background worker.
      const { data } = await api.post("/books/import?commit=false&preview_limit=500", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90 * 1000,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read file — try clicking 'Skip preview & import' below.");
      // Keep the file selection so user can retry with skip-preview
    } finally {
      setLoading(false);
    }
  };

  const commitSkipPreview = async () => {
    // Fast path for large files — go straight to the async background import
    // without running any preview. Confirmation is asked via a native dialog.
    if (!file) return;
    if (!window.confirm(
      `Skip preview and import '${file.name}' directly?\n\nAll valid rows will be added, invalid rows will be skipped. This is safe for any file size.`
    )) return;
    await commit();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    // Chunked import — parses xlsx/csv in browser, splits into 25K-row CSVs,
    // uploads each as its own async job. Bypasses Cloudflare 100 MB / 100 s limits.
    setJob({
      status: "queued", total: 0, processed: 0,
      added: 0, skipped: 0, errors: 0, smart_categorized: 0,
      chunkStates: [], phase: "parsing",
    });
    toast("Reading file in your browser…");
    try {
      await chunkedImport(file, {
        onSplit: ({ totalRows, chunkCount, chunkSize }) => {
          setJob((prev) => ({
            ...prev,
            phase: "uploading",
            total: totalRows,
            chunkCount,
            chunkSize,
          }));
          toast(`Split into ${chunkCount} chunk${chunkCount === 1 ? "" : "s"} of ~${chunkSize.toLocaleString()} rows each. Uploading…`);
        },
        onChunkQueued: ({ index, total }) => {
          setJob((prev) => ({ ...prev, status: "running", queued: index + 1, chunkCount: total }));
        },
        onProgress: (p) => {
          setJob((prev) => ({
            ...prev,
            status: "running",
            total: p.total,
            processed: p.processed,
            added: p.added,
            skipped: p.skipped,
            errors: p.errors,
            smart_categorized: p.smart,
            chunkStates: p.chunkStates,
          }));
        },
        onDone: ({ added, skipped, errors, chunkStates }) => {
          setJob((prev) => ({
            ...prev,
            status: "completed",
            added, skipped, errors,
            chunkStates,
          }));
          setCommitting(false);
          toast.success(`${added.toLocaleString()} books imported (${skipped} skipped, ${errors} errors)`);
          onImported?.();
        },
        onError: (msg) => {
          setJob((prev) => ({ ...prev, status: "failed", error_message: msg }));
          setCommitting(false);
          toast.error(msg);
        },
      });
    } catch (e) {
      setJob((prev) => ({ ...prev, status: "failed", error_message: e.message || "Import failed" }));
      setCommitting(false);
      toast.error(e.message || "Import failed");
    }
  };

  const rows = committed?.rows || preview?.rows || [];
  const summary = committed?.summary || preview?.summary;

  const downloadReport = () => {
    const csv = rowsToCSV(rows, [
      { key: "row_number", label: "row_number" },
      { key: "sku", label: "sku" },
      { key: "isbn", label: "isbn" },
      { key: "title", label: "title" },
      { key: "author", label: "author" },
      { key: "publisher", label: "publisher" },
      { key: "price", label: "price" },
      { key: "quantity", label: "quantity" },
      { key: "category", label: "category" },
      { key: "language", label: "language" },
      { key: "condition", label: "condition" },
      { key: "formats", label: "formats", get: (r) => Array.isArray(r.formats) ? r.formats.join("|") : (r.formats || "") },
      { key: "status", label: "status" },
      { key: "reason", label: "reason" },
    ]);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const kind = committed ? "committed" : "preview";
    downloadCSV(csv, `import-report-${kind}-${stamp}.csv`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="import-csv-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl text-[#1A1714]">Import inventory (CSV or Excel)</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Columns: SKU · ISBN · Title · Item Photo URL · Author · Publisher · BISAC · Pages · Format · Language · Condition · Description · Price · Quantity
          </DialogDescription>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021] mt-1">
            Tip: Include ISBN + BISAC — we auto-pick the category and enrich cover/description from Google Books &amp; Open Library.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("xlsx")}
              data-testid="import-template-xlsx"
              className="rounded-none border-[#1A1714] text-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-[10px] uppercase tracking-editorial"
            >
              <Download size={12} className="mr-1.5" /> Download Excel template
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("csv")}
              data-testid="import-template-csv"
              className="rounded-none border-[#E5E1D8] text-[#1A1714]/70 hover:bg-[#F0EBE0] font-mono text-[10px] uppercase tracking-editorial"
            >
              <Download size={12} className="mr-1.5" /> Download CSV template
            </Button>
          </div>
        </DialogHeader>

        {!preview && !committed && (
          <div className="p-10 flex-1 flex flex-col items-center justify-center">
            <label
              htmlFor="csv-input"
              className="cursor-pointer border-2 border-dashed border-[#E5E1D8] hover:border-[#1A1714] w-full max-w-xl p-16 text-center transition-colors"
              data-testid="csv-drop-area"
            >
              <Upload className="mx-auto text-[#1A1714]/40" size={40} />
              <p className="mt-4 font-serif text-2xl text-[#1A1714]">Drop a CSV or Excel file, or click to choose</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
                Accepts .csv, .xlsx · Rows missing Title or Price will be skipped · Existing SKUs will be skipped
              </p>
              <input
                id="csv-input"
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onFile}
                className="hidden"
                data-testid="csv-file-input"
              />
            </label>
            {loading && <p className="mt-6 font-serif italic text-lg text-[#1A1714]/60">Reading your file…</p>}

            {file && !loading && !preview && (
              <div className="mt-8 text-center" data-testid="csv-skip-preview-panel">
                <p className="font-mono text-xs text-[#1A1714]/70 mb-3">
                  Preview failed or your file is very large. You can still import safely.
                </p>
                <Button
                  onClick={commitSkipPreview}
                  disabled={committing || (job && ["queued","running"].includes(job.status))}
                  className="rounded-none bg-[#7A2021] text-[#F9F6F0] hover:bg-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                  data-testid="csv-skip-preview-commit"
                >
                  Skip preview &amp; import directly →
                </Button>
              </div>
            )}
          </div>
        )}

        {job && !preview && !committed && (
          <div className="px-6 py-5 border-b border-[#E5E1D8] bg-[#F0EBE0]/40" data-testid="import-job-progress-standalone">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {["queued","running"].includes(job.status) && <Loader2 size={14} className="animate-spin text-[#7A2021]" />}
                {job.status === "completed" && <CheckCircle2 size={14} className="text-green-700" />}
                {["failed","interrupted"].includes(job.status) && <XCircle size={14} className="text-red-700" />}
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/70">
                  {job.status === "queued" && "Queued — starting shortly…"}
                  {job.status === "running" && `Importing… ${job.processed?.toLocaleString() || 0} / ${job.total?.toLocaleString() || 0}`}
                  {job.status === "completed" && `Completed — ${job.added?.toLocaleString() || 0} books imported`}
                  {job.status === "failed" && `Failed — ${job.error_message || "unknown"}`}
                  {job.status === "interrupted" && "Interrupted — please re-upload"}
                </p>
              </div>
              {job.total > 0 && (
                <span className="font-mono text-[10px] text-[#1A1714]/60">
                  {Math.min(100, Math.round((job.processed / Math.max(1, job.total)) * 100))}%
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-[#E5E1D8] overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${["failed","interrupted"].includes(job.status) ? "bg-red-700" : "bg-[#7A2021]"}`}
                style={{ width: `${Math.min(100, Math.round((job.processed / Math.max(1, job.total)) * 100))}%` }}
              />
            </div>
            <div className="mt-2 grid grid-cols-4 gap-3 font-mono text-[10px] text-[#1A1714]/70">
              <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Added</span> <strong className="text-[#1A1714]">{(job.added||0).toLocaleString()}</strong></div>
              <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Skipped</span> <strong className="text-[#1A1714]">{(job.skipped||0).toLocaleString()}</strong></div>
              <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Errors</span> <strong className="text-[#1A1714]">{(job.errors||0).toLocaleString()}</strong></div>
              <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Auto-cat.</span> <strong className="text-[#1A1714]">{(job.smart_categorized||0).toLocaleString()}</strong></div>
            </div>
            {job.status === "completed" && (
              <Button onClick={close} className="mt-4 rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial" data-testid="csv-done-standalone">
                Done
              </Button>
            )}
          </div>
        )}

        {(preview || committed) && summary && (
          <>
            {preview?.truncated_preview && (
              <div className="px-6 py-3 border-b border-[#E5E1D8] bg-[#F0EBE0]" data-testid="csv-truncated-notice">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021]">
                  Large file · Preview shows first {preview.rows?.length || 500} rows.
                  Full file has <strong>{(preview.total_rows_in_file || summary.total)?.toLocaleString()}</strong> rows —
                  clicking &quot;Confirm import&quot; will process them all in the background.
                </p>
              </div>
            )}
            <div className="px-6 py-5 border-b border-[#E5E1D8] grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="csv-summary">
              <SummaryStat label="Total rows in file" value={preview?.total_rows_in_file || summary.total} testId="csv-summary-total" />
              <SummaryStat label={committed ? "Imported" : (preview?.truncated_preview ? "Valid (in preview)" : "Will import")} value={committed ? summary.added : summary.valid} accent testId="csv-summary-valid" />
              <SummaryStat label="Skipped (in preview)" value={summary.skipped} testId="csv-summary-skipped" />
              <div className="col-span-2 md:col-span-1">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">File</p>
                <p className="font-mono text-xs mt-1 truncate flex items-center gap-1.5">
                  <FileText size={12} />
                  {file?.name}
                </p>
              </div>
            </div>

            {summary.skip_reasons && Object.keys(summary.skip_reasons).length > 0 && (
              <div className="px-6 py-3 border-b border-[#E5E1D8] bg-[#7A2021]/5" data-testid="csv-skip-reasons">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#7A2021] mb-1">Skip reasons</p>
                <ul className="font-mono text-xs text-[#1A1714]/80 flex flex-wrap gap-x-6 gap-y-1">
                  {Object.entries(summary.skip_reasons).map(([reason, n]) => (
                    <li key={reason}><span className="text-[#7A2021]">{n}×</span> {reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {job && (
              <div className="px-6 py-4 border-b border-[#E5E1D8] bg-[#F0EBE0]/40" data-testid="import-job-progress">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {["queued","running"].includes(job.status) && <Loader2 size={14} className="animate-spin text-[#7A2021]" />}
                    {job.status === "completed" && <CheckCircle2 size={14} className="text-green-700" />}
                    {["failed","interrupted"].includes(job.status) && <XCircle size={14} className="text-red-700" />}
                    <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/70">
                      {job.status === "queued" && "Queued — starting shortly…"}
                      {job.status === "running" && `Importing… ${job.processed?.toLocaleString() || 0} / ${job.total?.toLocaleString() || 0}`}
                      {job.status === "completed" && `Completed — ${job.added?.toLocaleString() || 0} imported`}
                      {job.status === "failed" && `Failed — ${job.error_message || "unknown error"}`}
                      {job.status === "interrupted" && "Interrupted — please re-upload"}
                    </p>
                  </div>
                  {job.total > 0 && (
                    <span className="font-mono text-[10px] text-[#1A1714]/60" data-testid="import-job-pct">
                      {Math.min(100, Math.round((job.processed / Math.max(1, job.total)) * 100))}%
                    </span>
                  )}
                </div>
                <div className="w-full h-2 bg-[#E5E1D8] overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${job.status === "failed" || job.status === "interrupted" ? "bg-red-700" : "bg-[#7A2021]"}`}
                    style={{ width: `${Math.min(100, Math.round((job.processed / Math.max(1, job.total)) * 100))}%` }}
                    data-testid="import-job-bar"
                  />
                </div>
                <div className="mt-2 grid grid-cols-4 gap-3 font-mono text-[10px] text-[#1A1714]/70">
                  <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Added</span> <strong className="text-[#1A1714]">{(job.added||0).toLocaleString()}</strong></div>
                  <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Skipped</span> <strong className="text-[#1A1714]">{(job.skipped||0).toLocaleString()}</strong></div>
                  <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Errors</span> <strong className="text-[#1A1714]">{(job.errors||0).toLocaleString()}</strong></div>
                  <div><span className="text-[#1A1714]/50 uppercase tracking-editorial">Auto-categorized</span> <strong className="text-[#1A1714]">{(job.smart_categorized||0).toLocaleString()}</strong></div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-auto" data-testid="csv-preview-table">
              <table className="w-full text-xs font-mono">
                <thead className="sticky top-0 bg-[#F9F6F0] border-b border-[#E5E1D8]">
                  <tr className="text-left uppercase tracking-editorial text-[10px] text-[#1A1714]/60">
                    <th className="px-4 py-3 w-10">Row</th>
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">ISBN</th>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Author</th>
                    <th className="px-4 py-3 text-right">Price</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.row_number}
                      className={`border-b border-[#E5E1D8]/60 ${r.status === "skipped" ? "bg-[#7A2021]/5 text-[#1A1714]/60" : "text-[#1A1714]"}`}
                      data-testid={`csv-row-${r.row_number}`}
                    >
                      <td className="px-4 py-2">{r.row_number}</td>
                      <td className="px-4 py-2">
                        {r.status === "valid"
                          ? <CheckCircle2 size={14} className="text-[#1A1714]" />
                          : <XCircle size={14} className="text-[#7A2021]" />}
                      </td>
                      <td className="px-4 py-2">{r.sku || "—"}</td>
                      <td className="px-4 py-2">{r.isbn || "—"}</td>
                      <td className="px-4 py-2 font-serif text-sm">{r.title || <span className="italic text-[#7A2021]">missing</span>}</td>
                      <td className="px-4 py-2">{r.author || "—"}</td>
                      <td className="px-4 py-2 text-right">{r.price === "" || r.price == null ? <span className="italic text-[#7A2021]">missing</span> : (typeof r.price === "number" ? `$${r.price.toFixed(2)}` : `$${r.price}`)}</td>
                      <td className="px-4 py-2 text-right">{r.quantity ?? "—"}</td>
                      <td className="px-4 py-2 text-[#7A2021]">{r.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-5 border-t border-[#E5E1D8] flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
              {!committed ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 flex items-center gap-2">
                    <AlertCircle size={12} /> Review the preview above, then confirm to save.
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      onClick={downloadReport}
                      variant="outline"
                      className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                      data-testid="csv-download-report-preview"
                    >
                      <Download size={14} className="mr-2" /> Download report
                    </Button>
                    <Button onClick={reset} variant="outline" className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial" data-testid="csv-reset">
                      Choose another file
                    </Button>
                    <Button
                      onClick={commit}
                      disabled={committing || summary.valid === 0 || (job && ["queued","running"].includes(job.status))}
                      className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
                      data-testid="csv-commit"
                    >
                      {job && ["queued","running"].includes(job.status)
                        ? `Importing… ${(job.processed||0).toLocaleString()} / ${(job.total||0).toLocaleString()}`
                        : job && job.status === "completed"
                          ? "Import complete ✓"
                          : `Confirm import (${summary.valid}) →`}
                    </Button>
                    {job && job.status === "completed" && (
                      <Button onClick={close} className="rounded-none bg-[#7A2021] text-[#F9F6F0] hover:bg-[#1A1714] font-mono text-xs uppercase tracking-editorial" data-testid="csv-done">
                        Done
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="font-serif text-lg text-[#1A1714]">
                    Import complete. <em className="text-[#7A2021]">{summary.added}</em> books added, <em>{summary.skipped}</em> skipped.
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      onClick={downloadReport}
                      variant="outline"
                      className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                      data-testid="csv-download-report"
                    >
                      <Download size={14} className="mr-2" /> Download report
                    </Button>
                    <Button onClick={close} className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial" data-testid="csv-close">
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value, accent, testId }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</p>
      <p className={`font-serif text-4xl mt-1 ${accent ? "text-[#7A2021]" : "text-[#1A1714]"}`} data-testid={testId}>{value}</p>
    </div>
  );
}
